import type {
  FinancialBillingSelection,
  FinancialImport,
  FinancialLedgerEntry,
  FinancialLine,
  FinancialQuoteBilling,
  FinancialShipmentRecord,
} from '@/types/Models/Financial'
import { FINANCIAL_ARREARS_FROM } from '@/const/Financial'
import { financialDecimal, subtractFinancialAmounts } from '@/services/FinancialAmounts'
import { financialBillingId, financialLocalDate, financialReady, financialRole } from '@/services/FinancialImport'

export const financialBillingKey = (line: FinancialLine): string => `${line.batch.id}:${line.row.id}`

// Unbilled bookings carry forward. A known delivery cost must be reviewed first; a later fee alone does not
// replace the shipment's quote. Quotes are always SEK and are never counted as actual carrier costs.
export const financialQuoteCandidates = (
  entries: FinancialLedgerEntry[],
  history: FinancialQuoteBilling[],
  selection: FinancialBillingSelection,
): FinancialShipmentRecord[] => {
  const saved = new Map(history.map((item) => [item.itemId, item]))
  return entries
    .filter(({ shipment, lines }) => {
      const date = financialLocalDate(shipment.bookedAt)
      const previous = saved.get(shipment.itemId)
      return (
        shipment.billingId === selection.customerId &&
        !!date &&
        date >= FINANCIAL_ARREARS_FROM &&
        (!selection.to || date <= selection.to) &&
        shipment.bookedPrice !== null &&
        financialDecimal(shipment.bookedPrice) !== null &&
        Number(shipment.bookedPrice) >= 0 &&
        shipment.trackingNumbers.length > 0 &&
        !lines.some((line) => financialRole(line.batch, line.row) === 'base') &&
        (!previous ||
          (!selection.onlyNew &&
            !previous.appliedTo &&
            previous.billingId === shipment.billingId &&
            (!selection.from || financialLocalDate(previous.createdAt) >= selection.from)))
      )
    })
    .map(({ shipment }) => ({ ...shipment, bookedPrice: saved.get(shipment.itemId)?.amount ?? shipment.bookedPrice }))
}

// The reviewer decides the customer target. Imported actual costs never silently overwrite the quote or
// determine a new customer price. Only the difference from a previously exported target is billed again.
export const financialBillingPlan = (
  lines: FinancialLine[],
  quotedShipments: FinancialShipmentRecord[],
  history: FinancialQuoteBilling[],
  imports: FinancialImport[],
  audit: { createdAt: string; userName: string; fileName: string },
): { amounts: Record<string, { previous: string; amount: string }>; quotes: FinancialQuoteBilling[] } => {
  const quotes = new Map(history.map((item) => [item.itemId, { ...item }]))
  const amounts: Record<string, { previous: string; amount: string }> = {}
  for (const shipment of quotedShipments) {
    if (!quotes.has(shipment.itemId))
      quotes.set(shipment.itemId, {
        itemId: shipment.itemId,
        revision: 0,
        billingId: shipment.billingId,
        amount: shipment.bookedPrice!,
        currency: 'SEK',
        ...audit,
      })
  }
  const billedDeliveries = new Set<string>()
  for (const batch of imports) {
    const ids = new Set(batch.exports.filter((item) => item.kind !== 'review').flatMap((item) => item.rowIds ?? []))
    for (const row of batch.charges)
      if (row.match && ids.has(row.id) && financialRole(batch, row) === 'base') billedDeliveries.add(row.match.itemId)
  }
  // Stable ordering if several approved delivery rows arrive together. Existing duplicate-charge checks
  // still require the reviewer to decide which rows are valid; the quote is deducted from only one.
  for (const line of [...lines].sort((a, b) => financialBillingKey(a).localeCompare(financialBillingKey(b)))) {
    const { batch, row } = line
    if (!financialReady(batch, row)) throw new Error('Approve each customer amount before exporting.')
    const key = financialBillingKey(line)
    const billingId = financialBillingId(row.match)
    const record = [...batch.exports].reverse().find((item) => item.kind !== 'review' && item.rowIds?.includes(row.id))
    const snapshot = record?.snapshot?.[row.id]
    const target = row.review!.customerPrice!
    if (snapshot?.itemId && snapshot.itemId !== row.match!.itemId)
      throw new Error(
        'The shipment changed after billing. Reconcile the earlier shipment before exporting this row again.',
      )
    if (snapshot?.currency && snapshot.currency !== batch.currency)
      throw new Error('The currency changed after billing. Reconcile the earlier export before continuing.')
    if (snapshot && snapshot.billingId !== billingId)
      throw new Error(
        'The billing customer changed after export. Reconcile the earlier customer in Jeeves before billing this row again.',
      )
    if (record && !snapshot)
      throw new Error(
        'This older export has no saved customer amount. Reconcile it in Jeeves before billing this row again.',
      )
    const quote = quotes.get(row.match!.itemId)
    if (quote && (quote.billingId !== billingId || quote.currency !== batch.currency))
      throw new Error(
        'The earlier quoted billing uses another customer or currency. Reconcile it before exporting these charges; no currency conversion is applied.',
      )
    let previous = snapshot?.customerPrice ?? '0'
    if (snapshot && snapshot.customerPrice === null)
      throw new Error('The earlier customer amount is missing. Reconcile it before exporting again.')
    if (quote && !quote.appliedTo && financialRole(batch, row) === 'base' && !snapshot) {
      if (billedDeliveries.has(row.match!.itemId))
        throw new Error(
          'This shipment has both a quoted billing and an earlier delivery export. Reconcile its history before continuing.',
        )
      previous = quote.amount
      quote.appliedTo = key
    }
    const same = snapshot && target === snapshot.customerPrice
    // Explicit re-download reproduces the last export, including its debit/credit, instead of billing a full target.
    amounts[key] = {
      previous: same ? subtractFinancialAmounts(target, snapshot.billedAmount ?? target) : previous,
      amount: same ? (snapshot.billedAmount ?? target) : subtractFinancialAmounts(target, previous),
    }
  }
  return { amounts, quotes: [...quotes.values()] }
}
